import * as React from "react";
import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCollectFieldAdminStockMutation,
  useGetFieldAdminStockBalanceQuery,
  useGetFieldAdminVehiclesQuery,
} from "./fieldAdminApi";
import { showSuccess } from "../../Global/notifications/notificationsSlice";
import { CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { OK_GREEN_TEXT, BAD_RED_TEXT, MONO_FONT_FAMILY, formatTimeOfDay, teamLabel } from "./designTokens";
import CheckIcon from "@mui/icons-material/Check";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { FormPage, ScanViewport, parseDeviceQr, FAFieldRow, FAFormInput } from "./StockFormShell";
import { useConfirm } from "./FAControls";
import { FANotesField } from "./FALayout";

// The design caps this list at "Last 5" — it is a check on the scan you just made,
// not the session log; the full count lives in the counter above it.
const RECENT_LIMIT = 5;

export default function StockCollect() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { ask, confirmDialog } = useConfirm();

  const { data: balance } = useGetFieldAdminStockBalanceQuery();
  const { data: fleet } = useGetFieldAdminVehiclesQuery();
  const van = (fleet?.vehicles || []).find(
    (v) => v.assigned_team_id === fleet?.team_id && v.status !== "retired",
  );
  const [collectStock, { isLoading, error }] = useCollectFieldAdminStockMutation();

  // A collect run is a batch: the FA scans a vanload and confirms once, rather than
  // filling in a form per device as this page used to require.
  const [scanned, setScanned] = useState([]);
  const [manualSerial, setManualSerial] = useState("");
  const [notes, setNotes] = useState("");
  const [duplicate, setDuplicate] = useState(null);
  const [batchError, setBatchError] = useState(null);

  const expected = balance?.allocated != null ? Math.max(balance.allocated - balance.collected, 0) : null;

  // Takes either a scanned QR (a URL carrying serial and IMSI) or a typed serial.
  const addDevice = (raw) => {
    const { serial, imsi } = parseDeviceQr(raw);
    if (!serial) return;
    if (scanned.some((row) => row.serial === serial)) {
      setDuplicate(serial);
      return;
    }
    setDuplicate(null);
    setScanned((prev) => [{ serial, imsi, at: new Date() }, ...prev]);
  };

  const removeSerial = (serial) => setScanned((prev) => prev.filter((row) => row.serial !== serial));

  const handleSubmit = async () => {
    const proceed = await ask({
      title: "Confirm collection?",
      body: `This signs the collect trail for ${scanned.length} scanned device${scanned.length === 1 ? "" : "s"}.`,
      confirmLabel: "Confirm collection",
    });
    if (!proceed) return;

    setBatchError(null);

    // One POST per device: StockMovement is a per-device row and there is no batch
    // endpoint, so the batch is a client-side session over the existing write.
    const results = await Promise.all(scanned.map((row) => collectStock({
      device_serial: row.serial,
      device_imsi: row.imsi || undefined,
      notes: notes || undefined,
    })));

    // The mutation hook's own `error` only reflects the last request to start, so a
    // partial failure could leave it undefined and the button looking inert. Worse,
    // pressing Confirm again re-sent the devices that had already saved — the endpoint
    // does not dedupe by serial, so the van count over-counted. Keeping only the failures
    // makes the list honest and makes a retry send exactly the outstanding ones.
    const failed = scanned.filter((_row, index) => results[index].error);
    if (failed.length) {
      const saved = results.length - failed.length;
      setScanned(failed);
      setBatchError(
        `${saved} of ${results.length} device${results.length === 1 ? "" : "s"} collected. ` +
        `${failed.length} failed and ${failed.length === 1 ? "is" : "are"} still listed — ` +
        `press Confirm to retry just those.`
      );
      return;
    }

    dispatch(showSuccess(`${scanned.length} device${scanned.length === 1 ? "" : "s"} collected`));
    navigate("/field-admin/stock/");
  };

  return (
    <FormPage
      eyebrow="Step 1 of stock workflow"
      title="Scan to Collect"
      subtitle="Scan team-allocated stock onto the van · the FA signs the trail."
      onSubmit={handleSubmit}
      submitLabel={isLoading ? "Saving…" : "Confirm collection"}
      submitDisabled={isLoading || scanned.length === 0}
      summary={
        expected != null
          ? `${scanned.length} scanned · ${Math.max(expected - scanned.length, 0)} remaining`
          : `${scanned.length} scanned`
      }
      error={batchError || error?.data?.error_message}
    >
      {/* .context-strip — a scan is a transfer between two places, and the screen named
          neither: which company's counter the stock is leaving, and which van it is going on.
          Both matter at the counter, where the wrong van is an easy mistake to make. */}
      <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {[
          balance?.team_name
            ? {
                key: "ims",
                Icon: WarehouseIcon,
                label: (
                  <>
                    IMS stock area ·{" "}
                    <b>
                      {[balance.installer_company_name, teamLabel(balance.team_name, balance.team_id)]
                        .filter(Boolean).join(" — ")}
                    </b>
                  </>
                ),
              }
            : null,
          van ? { key: "van", Icon: LocalShippingIcon, label: `Van ${van.registration}`, muted: true } : null,
        ].filter(Boolean).map((chip) => (
          <Box key={chip.key} sx={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            px: "12px", py: "6px", borderRadius: "999px", fontWeight: 500, fontSize: "0.74rem",
            backgroundColor: chip.muted ? "rgba(255,255,255,0.04)" : "rgba(253,185,64,0.08)",
            border: `1px solid ${chip.muted ? "rgba(255,255,255,0.08)" : "rgba(253,185,64,0.22)"}`,
            color: chip.muted ? "rgba(255,255,255,0.7)" : "rgba(253,185,64,0.95)",
            "& b": { color: "#fff", fontWeight: 600, ml: "2px" },
          }}>
            <chip.Icon sx={{ fontSize: "0.8rem" }} />
            {chip.label}
          </Box>
        ))}
      </Box>

      <ScanViewport
        tone="amber"
        onScan={addDevice}
        hint="Camera viewport · point at a device QR code"
        scannedLabel={scanned.length ? `Scanned · ${scanned.length}` : null}
      />

      {expected != null ? (
        <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", p: "14px 18px" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "14px", mb: "9px" }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Scanned
            </Typography>
            {/* The count is the accent — it is the one number on the screen that changes as
                you work, and it read as ordinary body text in white. */}
            <Typography sx={{ fontSize: "1rem", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
              <Box component="span" sx={{ color: AMBER, fontSize: "1.4rem" }}>{scanned.length}</Box>
              <Box component="span" sx={{ color: "rgba(255,255,255,0.5)", fontWeight: 500, ml: "2px" }}> / {expected} expected</Box>
            </Typography>
          </Box>
          <Box sx={{ height: "6px", backgroundColor: "rgba(255,255,255,0.06)", borderRadius: "3px", overflow: "hidden" }}>
            <Box sx={{ height: "100%", width: `${expected ? Math.min(100, (scanned.length / expected) * 100) : 0}%`, background: `linear-gradient(90deg, ${AMBER}, #E0A020)`, borderRadius: "3px" }} />
          </Box>
        </Box>
      ) : null}

      {duplicate ? (
        <Box sx={{ backgroundColor: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.25)", borderRadius: "10px", px: "14px", py: "10px" }}>
          <Typography sx={{ fontSize: "0.8rem", color: "#FBBF24" }}>
            Already scanned in this session · {duplicate}
          </Typography>
        </Box>
      ) : null}

      {/* .manual — a label above a monospace field, not a floating-label control. Serials
          are read off a device and typed digit by digit; a proportional font makes a
          transposed 0/O or 1/l invisible, which is the one mistake this screen must not make. */}
      <FAFieldRow label="Manual entry · Serial or IMSI" htmlFor="manual-serial">
        <FAFormInput
          mono
          id="manual-serial"
          value={manualSerial}
          onChange={(e) => setManualSerial(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { addDevice(manualSerial); setManualSerial(""); } }}
          placeholder="23000412 or 655021000412"
        />
      </FAFieldRow>

      {/* .section-label — outside the card, with the cap on the right. The list is the last
          few scans as a check, not the whole session: at 26 devices the full list buries the
          scan you just made, which is the only one you are checking. */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pt: "4px", pb: "12px" }}>
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>
            Recently scanned
          </Typography>
          {/* Sentence case and lighter than the label it sits opposite — a caption on the
              list, not a second heading. Nothing to cap before the first scan. */}
          {scanned.length ? (
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 500, color: "rgba(255,255,255,0.4)", textTransform: "none", letterSpacing: 0 }}>
              {scanned.length > RECENT_LIMIT ? `Last ${RECENT_LIMIT} of ${scanned.length}` : `Last ${scanned.length}`}
            </Typography>
          ) : null}
        </Box>

        {/* The card stays whether or not anything has been scanned, with the message inside
            it — the design's empty state is a child of .scanned-list, not a replacement for
            it. Hiding the whole section made the page reflow the moment the first scan
            landed, and left the screen with no sign that a list was coming. */}
        <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden" }}>
          {scanned.length === 0 ? (
            <Typography sx={{ textAlign: "center", color: "rgba(255,255,255,0.55)", fontSize: "0.95rem", p: "80px 20px" }}>
              Nothing scanned yet — scan a device QR or enter a serial to begin.
            </Typography>
          ) : scanned.slice(0, RECENT_LIMIT).map((row, index, shown) => (
            <Box key={row.serial} sx={{
              display: "flex", alignItems: "center", gap: "12px", p: "11px 16px",
              borderBottom: index === shown.length - 1 ? "none" : "1px solid rgba(255,255,255,0.04)",
            }}>
              {/* Zero-padded so the column stays aligned as the count crosses ten. */}
              <Box component="span" sx={{ fontFamily: MONO_FONT_FAMILY, fontSize: "0.78rem", color: "rgba(255,255,255,0.5)", width: "24px", flexShrink: 0 }}>
                {String(scanned.length - index).padStart(2, "0")}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: MONO_FONT_FAMILY, fontSize: "0.86rem", color: "#fff", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {row.serial}
                </Typography>
                {row.imsi ? (
                  <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", mt: "1px" }}>
                    IMSI {row.imsi}
                  </Typography>
                ) : null}
              </Box>
              <Typography sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.5)", fontVariantNumeric: "tabular-nums" }}>
                {formatTimeOfDay(row.at)}
              </Typography>
              {/* .scan-check — a filled disc after the time, reading as "this one landed",
                  rather than a bare tick leading the row. */}
              <Box sx={{
                width: "22px", height: "22px", borderRadius: "50%", flexShrink: 0,
                backgroundColor: "rgba(34,197,94,0.18)", color: OK_GREEN_TEXT,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}>
                <CheckIcon sx={{ fontSize: "0.8rem" }} />
              </Box>
              <Box
                component="button"
                type="button"
                aria-label={`Remove ${row.serial}`}
                onClick={() => removeSerial(row.serial)}
                sx={{
                  width: "24px", height: "24px", borderRadius: "6px", ml: "4px", flexShrink: 0,
                  background: "transparent", border: "1px solid rgba(255,255,255,0.1)",
                  color: "rgba(255,255,255,0.5)", cursor: "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  "&:hover": { color: BAD_RED_TEXT, borderColor: "rgba(239,68,68,0.4)", backgroundColor: "rgba(239,68,68,0.08)" },
                }}
              >
                <CloseIcon sx={{ fontSize: "0.8rem" }} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box>
        <Typography sx={{ fontSize: "0.74rem", fontWeight: 600, color: "rgba(255,255,255,0.55)", mb: "6px" }}>Notes (optional)</Typography>
        <FANotesField
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Open-format notes captured against today's collect log."
          minHeight="70px"
        />
      </Box>

      {confirmDialog}
    </FormPage>
  );
}
